import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { CurrencyRateService } from '@gelato-api-ui/core/currency-rate/services/currency-rate.service';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { logEventV2 } from '@gelato-api-ui/core/analytics/helpers/trackEvent';
import { getSelectedClientCountry } from '@api-ui-app/src/app/ngrx/auth.reducer';
import { Store } from '@ngrx/store';
import {
  getIsProductPricesLoading,
  getMode,
  getPricesTableData,
  getProduct,
  getProductDraftPrices,
  getSelectedPricesCountry,
  getSelectedPricesCurrency,
  getStoreProductNewVariantsUids,
} from '@api-ui-app/src/app/ngrx/e-commerce-product-wizard.selector';
import { AuthService } from '@gelato-api-ui/core/auth/auth.service';
import {
  PriceNavigatorDraftPrices,
  PriceNavigatorItemModel,
  PriceNavigatorStoreModel,
} from '@api-ui-app/src/app/price-navigator/prices/types/price-navigator.types';
import {
  discardDraftProductPrices,
  loadProductPrices,
  loadUnpublishedProductPrices,
  setDraftProductPrices,
  setPricesCountry,
  setPricesCurrency,
} from '@api-ui-app/src/app/ngrx/e-commerce-product-wizard.actions';
import { PriceCollection } from '@api-ui-app/src/app/ngrx/e-commerce-product-wizard.reducer';
import { setSelectedStore } from '@api-ui-app/src/app/ngrx/e-commerce-stores.actions';
import { getStore } from '@api-ui-app/src/app/ngrx/e-commerce-stores.selector';
import {
  PRICE_NAVIGATOR_TABLE_DEFAULT_VISIBLE_COLUMNS,
  PriceNavigatorTableColumn,
} from '@api-ui-app/src/app/shared/pricing-table/constants/price-navigator.constants';
import { StorageService } from '@gelato-api-ui/sdk/src/lib/storage/storage.service';
import { EstimatedTaxesDisclaimerModalComponent } from '@api-ui-app/src/app/shared/pricing-table/estimated-taxes-disclaimer-modal/estimated-taxes-disclaimer-modal.component';
import { PriceTableContextName } from '@api-ui-app/src/app/shared/pricing-table/constants/price-table-context-name';
import { PlatformCostsModalComponent } from '@api-ui-app/src/app/shared/pricing-table/platform-costs-modal/platform-costs-modal.component';
import { EStore } from '@gelato-api-ui/core/e-commerce/e-store';
import { EProductWizardMode } from '@gelato-api-ui/core/e-commerce/e-product-wizard-mode.enum';
import { EStoreType } from '@gelato-api-ui/core/e-commerce/e-store-type.enum';
import { DialogService } from '@gelato-api-ui/sdk/src/lib/shared/dialog/dialog.service';

@Injectable()
export class ECommerceStoresNewPriceTableFacade {
  product$ = this.store.select(getProduct);
  selectedStore$ = this.store.select(getStore);
  priceNavigatorStores$: Observable<PriceNavigatorStoreModel[]> = this.selectedStore$.pipe(
    filter(Boolean),
    map(({ id, name, type, currency, status }: EStore) => [
      {
        storeId: id,
        name,
        type,
        currency,
        status,
      },
    ]),
  );
  storeCurrency$ = this.selectedStore$.pipe(map(store => store?.currency));
  clientId$ = this.authService.getClientId();
  selectedCurrency$ = combineLatest([this.store.select(getSelectedPricesCurrency), this.storeCurrency$]).pipe(
    map(([selectedCurrency, storeCurrency]) => selectedCurrency || storeCurrency),
  );
  suggestedCountry$ = this.store.select(getSelectedClientCountry);
  selectedCountry$ = combineLatest([this.store.select(getSelectedPricesCountry), this.suggestedCountry$]).pipe(
    map(([selectedCountry, clientCountry]) => selectedCountry || clientCountry),
  );
  tableData$: Observable<PriceNavigatorItemModel[]> = this.store.select(getPricesTableData);
  draftPrices$: Observable<PriceNavigatorDraftPrices[]> = this.store.select(getProductDraftPrices);
  isPricesLoading$: Observable<boolean> = this.store.select(getIsProductPricesLoading);
  isCurrencyRateLoading$ = this.currencyRateService.hasActiveRequests$;
  displayedTableColumns$: BehaviorSubject<PriceNavigatorTableColumn[]> = new BehaviorSubject([]);
  selectedNewVariantsUids$: Observable<string[]> = this.store.select(getStoreProductNewVariantsUids);
  mode$ = this.store.select(getMode);

  contextName = PriceTableContextName.EcommPricingTable;

  private readonly displayedTableColumnsLocalStorageKey = 'e-comm-price-table-displayed-columns';

  constructor(
    private readonly authService: AuthService,
    private readonly currencyRateService: CurrencyRateService,
    private readonly store: Store,
    private readonly storageService: StorageService,
    private readonly dialogService: DialogService,
  ) {}

  loadProductPrices() {
    combineLatest([
      this.clientId$,
      this.product$,
      this.selectedCountry$,
      this.selectedCurrency$,
      this.selectedNewVariantsUids$,
      this.mode$,
    ])
      .pipe(
        filter(([clientId, product, country, currency]) => !!clientId && !!country && !!currency),
        take(1),
      )
      .subscribe(([clientId, product, country, currency, selectedNewVariantsUids, mode]) => {
        const isTemplateCreationMode = mode === EProductWizardMode.CREATE_PRODUCT_TEMPLATE;
        const payload = {
          clientId,
          productId: !isTemplateCreationMode ? product.id : product.productName, // Set product name as productId for fetching template unpublished prices via "{id, productUids: []}" query structure
          storeId: product.storeId,
          country,
          currency,
          unpublishedVariantsUids: !product.hasDraft ? selectedNewVariantsUids : [],
        };

        if (isTemplateCreationMode) {
          this.store.dispatch(
            loadUnpublishedProductPrices({ payload: { ...payload, storeType: EStoreType.TEMPLATE } }),
          );
        } else {
          this.store.dispatch(loadProductPrices({ payload }));
        }
      });
  }

  countryChange(countryIsoCode: string) {
    logEventV2('Price Settings Country Changed', {
      countryIsoCode,
      origin: this.contextName,
    });
    this.store.dispatch(setPricesCountry({ payload: countryIsoCode }));
    this.loadProductPrices();
  }

  currencyChange(currency: string, doTrackEvent = true) {
    if (doTrackEvent) {
      logEventV2('Price Settings Currency Changed', {
        currency,
        origin: this.contextName,
      });
    }
    this.setCurrency(currency);
  }

  setDraftPrices(draftPrices: PriceNavigatorDraftPrices[]) {
    combineLatest([this.selectedCurrency$, this.storeCurrency$])
      .pipe(
        switchMap(([selectedCurrency, storeCurrency]) =>
          this.currencyRateService.get(selectedCurrency, storeCurrency).pipe(
            take(1),
            map(currencyRate =>
              draftPrices.map(price => ({
                ...price,
                newRetailPriceInStoreCurrency: price.newRetailPrice * currencyRate,
                selectedCurrency,
              })),
            ),
            tap(prices => {
              this.store.dispatch(setDraftProductPrices({ payload: prices }));
            }),
          ),
        ),
      )
      .subscribe();
  }

  discardDraftPrices() {
    this.store.dispatch(discardDraftProductPrices());
  }

  getPricesInStoreCurrency(draftPrices: PriceNavigatorDraftPrices[]): PriceCollection {
    const priceCollection: PriceCollection = {};
    draftPrices.forEach(({ productUid, newRetailPriceInStoreCurrency }) => {
      priceCollection[productUid] = newRetailPriceInStoreCurrency;
    });

    return priceCollection;
  }

  setStoreFromStatePayload() {
    this.selectedStore$
      .pipe(
        take(1),
        filter(store => !store), // load only when store is not set yet
        switchMap(() => this.store.select(getStore)),
      )
      .subscribe(store => {
        this.store.dispatch(setSelectedStore({ payload: store }));
      });
  }

  loadDisplayedTableColumns() {
    const displayedColumns: PriceNavigatorTableColumn[] =
      this.storageService.getItem(this.displayedTableColumnsLocalStorageKey) ||
      PRICE_NAVIGATOR_TABLE_DEFAULT_VISIBLE_COLUMNS;

    this.setDisplayedTableColumns(displayedColumns);
  }

  setDisplayedTableColumns(displayedColumns: PriceNavigatorTableColumn[], addedColumn?: PriceNavigatorTableColumn) {
    this.confirmTableColumnVisibility(addedColumn)
      .pipe(take(1), filter(Boolean))
      .subscribe(() => {
        this.storageService.setItem(this.displayedTableColumnsLocalStorageKey, displayedColumns);
        this.displayedTableColumns$.next(displayedColumns);
      });
  }

  private confirmTableColumnVisibility(column) {
    if (column === PriceNavigatorTableColumn.EstimatedTaxes) {
      return this.dialogService
        .open(EstimatedTaxesDisclaimerModalComponent, {
          data: { contextName: PriceTableContextName.EcommPricingTable },
        })
        .afterClosed();
    }

    if (column === PriceNavigatorTableColumn.PlatformCosts) {
      return combineLatest([this.priceNavigatorStores$, this.selectedCurrency$]).pipe(
        switchMap(([stores, currency]) =>
          this.dialogService.open(PlatformCostsModalComponent, { data: { stores, currency } }).afterClosed(),
        ),
      );
    }

    return of(true);
  }

  private setCurrency(currency: string) {
    this.store.dispatch(setPricesCurrency({ payload: currency }));
    this.loadProductPrices();
  }
}
